import { ImagePlus, RefreshCw, ServerCog, Sparkles, X } from "lucide-react";
import type { ChangeEvent, CSSProperties } from "react";
import { useMemo, useState } from "react";
import { fetchFaviconFile, upscaleImageFile, upscaleWithServer } from "../utils/iconUpscale";

type IconUpscalePanelProps = {
  pageUrl: string;
  accent: string;
  customIconDataUrl?: string;
  iconSourceUrl?: string;
  onChange: (dataUrl: string | undefined, sourceUrl?: string) => void;
};

export function IconUpscalePanel({
  pageUrl,
  accent,
  customIconDataUrl,
  iconSourceUrl,
  onChange
}: IconUpscalePanelProps) {
  const [message, setMessage] = useState("URLからサーバー側でfaviconを取得し、512pxアイコンとして保存できます。");
  const [busy, setBusy] = useState<"favicon" | "file" | "server" | null>(null);

  const hasIcon = Boolean(customIconDataUrl);
  const canFetchFavicon = useMemo(() => {
    try {
      const parsed = new URL(pageUrl);
      return ["http:", "https:"].includes(parsed.protocol);
    } catch {
      return false;
    }
  }, [pageUrl]);

  const applyFile = async (file: File, sourceUrl?: string) => {
    const icon = await upscaleImageFile(file, sourceUrl);
    onChange(icon.dataUrl, icon.sourceUrl);
    setMessage(`${icon.width}x${icon.height} から512pxアイコンを作成しました。`);
  };

  const fetchFavicon = async () => {
    if (!canFetchFavicon) {
      setMessage("先に http または https のURLを入力してください。");
      return;
    }
    setBusy("favicon");
    try {
      const { file, sourceUrl } = await fetchFaviconFile(pageUrl);
      await applyFile(file, sourceUrl);
    } catch (error) {
      const detail = error instanceof Error ? error.message : "URLを確認するか画像を選択してください。";
      setMessage(`サーバー側でfaviconを取得できませんでした。${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy("file");
    try {
      await applyFile(file);
    } catch {
      setMessage("この画像は読み込めませんでした。PNG/JPG/WebPを指定してください。");
    } finally {
      setBusy(null);
    }
  };

  const runServerUpscale = async () => {
    if (!customIconDataUrl) {
      setMessage("先にfavicon取得または画像アップロードでアイコンを作成してください。");
      return;
    }
    setBusy("server");
    try {
      const output = await upscaleWithServer(customIconDataUrl);
      onChange(output, iconSourceUrl);
      setMessage("サーバー側で125 Real-ESRGANへ中継し、高品質化したアイコンを保存しました。");
    } catch {
      setMessage("サーバー側125高品質化は使えませんでした。ローカル512px版を保持します。");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="upscale-panel" aria-label="faviconアップサンプリング">
      <div className="upscale-preview" style={{ "--accent": accent } as CSSProperties}>
        {customIconDataUrl ? <img src={customIconDataUrl} alt="" /> : <Sparkles size={28} aria-hidden="true" />}
      </div>

      <div className="upscale-controls">
        <div className="upscale-actions">
          <button type="button" onClick={fetchFavicon} disabled={busy !== null}>
            <RefreshCw size={16} />
            URLからfavicon取得
          </button>
          <label className="file-button">
            <ImagePlus size={16} />
            画像を選択
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} disabled={busy !== null} />
          </label>
          {hasIcon && (
            <button type="button" onClick={() => onChange(undefined, undefined)} disabled={busy !== null}>
              <X size={16} />
              クリア
            </button>
          )}
        </div>

        <button type="button" className="server-upscale-button" onClick={runServerUpscale} disabled={busy !== null || !hasIcon}>
          <ServerCog size={16} />
          サーバー側で125高品質化
        </button>

        <p className="upscale-message" role="status">
          {busy ? "処理中..." : message}
        </p>
      </div>
    </section>
  );
}
